/**
 * FieldComparisonRow - Shows a single field with values from multiple sources
 * User can select which value to use for the result
 */
import { Group, Text, Radio, Stack, Paper } from '@mantine/core';

interface FieldValue {
  source: string; // "canonical" | "LiveAquaria" | "AbyssAquatics" | "TMC"
  value: string | number | null;
  label: string; // Display label for the source
}

interface FieldComparisonRowProps {
  fieldName: string;
  fieldLabel: string;
  values: FieldValue[];
  selectedSource: string;
  onSelect: (source: string) => void;
  showOnlyDifferences?: boolean;
}

export function FieldComparisonRow({
  fieldName,
  fieldLabel,
  values,
  selectedSource,
  onSelect,
  showOnlyDifferences = false,
}: FieldComparisonRowProps) {
  // Check if all values are the same
  const uniqueValues = new Set(values.map((v) => String(v.value ?? '')));
  const allSame = uniqueValues.size === 1;

  // If showing only differences and all values are same, hide this row
  if (showOnlyDifferences && allSame) {
    return null;
  }

  return (
    <Paper p="sm" withBorder mb="xs" data-testid={`field-row-${fieldName}`}>
      <Stack gap="xs">
        <Text fw={600} size="sm" c="dimmed">
          {fieldLabel}
        </Text>
        <Radio.Group value={selectedSource} onChange={onSelect}>
          <Stack gap="xs">
            {values.map((v) => (
              <Radio
                key={v.source}
                value={v.source}
                label={
                  <Group gap="xs">
                    <Text size="xs" c="dimmed" w={100}>
                      {v.label}:
                    </Text>
                    <Text size="sm" lineClamp={2}>
                      {v.value !== null && v.value !== '' ? (
                        String(v.value)
                      ) : (
                        <Text span c="dimmed" fs="italic">
                          empty
                        </Text>
                      )}
                    </Text>
                  </Group>
                }
              />
            ))}
          </Stack>
        </Radio.Group>
      </Stack>
    </Paper>
  );
}
